import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';

interface EndScenarioImpactBotsDialogProps {
  open: boolean;
  /** Current number of running bots; pre-fills the field and is used as the fallback on invalid input. */
  defaultImpactBots: number;
  onCancel: () => void;
  onConfirm: (impactBots: number) => void;
}

/**
 * Prompts the operator to set the "impact bots" count before an End Scenario request is sent.
 * Defaults to the current running bot count; the confirmed value is recorded on the execution(s)
 * being stopped.
 */
const EndScenarioImpactBotsDialog: React.FC<EndScenarioImpactBotsDialogProps> = ({
  open,
  defaultImpactBots,
  onCancel,
  onConfirm,
}) => {
  const [impactBotsInput, setImpactBotsInput] = useState(String(defaultImpactBots));

  useEffect(() => {
    if (open) {
      setImpactBotsInput(String(defaultImpactBots));
    }
  }, [open, defaultImpactBots]);

  const parsedImpactBots = Number(impactBotsInput);
  const isValid =
    impactBotsInput.trim() !== '' && Number.isFinite(parsedImpactBots) && parsedImpactBots >= 0;

  const handleConfirm = () => {
    onConfirm(isValid ? Math.trunc(parsedImpactBots) : defaultImpactBots);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      PaperProps={{
        sx: {
          backgroundColor: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
          border: (theme) =>
            `2px solid ${
              theme.palette.mode === 'dark' ? 'rgba(255, 51, 102, 0.4)' : 'rgba(220, 38, 38, 0.28)'
            }`,
          borderRadius: '12px',
          minWidth: '380px',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 700,
          color: 'error.main',
          letterSpacing: '0.05em',
        }}
      >
        END SCENARIO
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'text.primary', mb: 2 }}>
          Set the number of bots that had impact during this execution. Defaults to the current
          number of running bots and is recorded on the execution.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          type="number"
          label="Impact Bots"
          value={impactBotsInput}
          onChange={(e) => setImpactBotsInput(e.target.value)}
          inputProps={{ min: 0, step: 1 }}
          sx={{ fontFamily: '"Space Mono", monospace' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} sx={{ fontFamily: '"Space Mono", monospace' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!isValid}
          sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}
        >
          End Scenario
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndScenarioImpactBotsDialog;
